import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpenCheck,
  CalendarClock,
  Check,
  ChevronDown,
  ClipboardCopy,
  Info,
  Volume2,
} from "lucide-react";
import BackButton from "components/ui/back-button";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/ui/accordion";
import {
  DEMO_CALLS,
  INBOUND_DEMO_TIMEZONE,
  knowledgeEntryById,
  knowledgeSourceLabel,
  practiceNameById,
  practiceShortNameById,
} from "./mockData";
import {
  buildHandoffNote,
  citedKnowledgeEntryIds,
  formatCallTime,
  REASON_LABELS,
} from "./selectors";
import OutcomeBadge from "./OutcomeBadge";
import DemoDataBanner from "./DemoDataBanner";
import { KNOWLEDGE_BANK_PATH, knowledgeEntryPath } from "./KnowledgeBankPage";
import { APPOINTMENT_TYPES_PATH } from "./AppointmentTypesCard";

function formatOffset(offsetSeconds: number): string {
  const minutes = Math.floor(offsetSeconds / 60);
  const seconds = String(offsetSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function InboundCallDetail() {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();

  const call = useMemo(
    () => DEMO_CALLS.find((c) => c.id === callId),
    [callId]
  );

  // Demo-only workflow state: lives in React state, resets on reload.
  const [taskDone, setTaskDone] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expandedCitations, setExpandedCitations] = useState<
    Record<number, boolean>
  >({});

  const toggleCitation = (turnIndex: number) =>
    setExpandedCitations((current) => ({
      ...current,
      [turnIndex]: !current[turnIndex],
    }));

  const goBack = () => navigate("/dashboard/inbound-calls");

  if (!call) {
    return (
      <div className="p-6">
        <BackButton onClick={goBack} />
        <p className="mt-4 text-sm text-gray-600">
          Call not found in the demo data.
        </p>
      </div>
    );
  }

  const practiceName = practiceNameById(call.practiceId);
  const citedEntries = citedKnowledgeEntryIds(call).map(knowledgeEntryById);

  const copyHandoffNote = () => {
    const note = buildHandoffNote(call, practiceName, INBOUND_DEMO_TIMEZONE);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(note).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="w-full bg-background">
      <div className="border-b-2 border-gray-100 px-4 py-2 sticky top-0 bg-white z-10 flex items-center justify-between gap-2">
        <BackButton onClick={goBack} />
        <DemoDataBanner />
      </div>

      <div className="p-4 space-y-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              {call.callerName}
            </h2>
            <p className="text-xs text-gray-500">
              {formatCallTime(call.receivedAt, INBOUND_DEMO_TIMEZONE)} ·{" "}
              {practiceName} · {REASON_LABELS[call.reason]}
            </p>
          </div>
          <OutcomeBadge outcome={call.outcome} />
        </div>

        {/* Recap and next step — most scannable position */}
        <Card className="gap-2 py-4 border-l-4 border-l-brand">
          <CardContent className="px-4 space-y-3">
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">
                Recap
              </h3>
              <p className="text-sm text-gray-800" data-testid="call-recap">
                {call.recapText}
              </p>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">
                Next step
              </h3>
              <p className="text-sm text-gray-800">{call.nextStep}</p>
            </div>
            <div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={copyHandoffNote}
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <ClipboardCopy className="h-3 w-3 mr-1" />
                    Copy handoff note
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Caller information */}
        <Card className="gap-2 py-4">
          <CardHeader className="px-4">
            <CardTitle className="text-sm">Caller information</CardTitle>
          </CardHeader>
          <CardContent className="px-4">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <dt className="text-xs text-gray-500">Caller</dt>
                <dd className="text-gray-800">{call.callerName}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Phone (synthetic)</dt>
                <dd className="text-gray-800">{call.callerPhone}</dd>
              </div>
              {call.patientName && (
                <div>
                  <dt className="text-xs text-gray-500">Patient</dt>
                  <dd className="text-gray-800">{call.patientName}</dd>
                </div>
              )}
              {call.callerRelationship && (
                <div>
                  <dt className="text-xs text-gray-500">Relationship</dt>
                  <dd className="text-gray-800">{call.callerRelationship}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs text-gray-500">Practice</dt>
                <dd className="text-gray-800">{practiceName}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* Cancel request note — cancellation requires staff action. */}
        {call.outcome === "APPOINTMENT_REQUESTED" && (
          <Card className="gap-2 py-4 bg-blue-50 border-blue-200">
            <CardContent className="px-4 flex items-start gap-2">
              <CalendarClock className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-900">
                The assistant captured this cancellation request; cancellation is
                completed by staff. Appointment scheduling is handled by
                software, but deletions require manual processing.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Scheduled appointment note — booked automatically by the assistant. */}
        {call.outcome === "APPOINTMENT_SCHEDULED" && (
          <Card className="gap-2 py-4 bg-emerald-50 border-emerald-200">
            <CardContent className="px-4 space-y-2">
              <div className="flex items-start gap-2">
                <CalendarClock className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
                <p className="text-xs text-emerald-900">
                  The assistant scheduled this appointment directly — no staff
                  action required.
                </p>
              </div>
              {call.appointmentType && (
                <div className="flex items-center gap-2 ml-6">
                  <Link to={APPOINTMENT_TYPES_PATH}>
                    <Badge
                      variant="outline"
                      className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs hover:bg-emerald-200 cursor-pointer"
                    >
                      {call.appointmentType}
                    </Badge>
                  </Link>
                  <span className="text-[11px] text-emerald-700">
                    Detected from{" "}
                    <Link
                      to={APPOINTMENT_TYPES_PATH}
                      className="underline hover:text-emerald-900"
                    >
                      appointment types
                    </Link>
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Knowledge attribution — the minimal two-tier knowledge surface */}
        <Card className="gap-2 py-4">
          <CardHeader className="px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <BookOpenCheck className="h-4 w-4 text-gray-500" />
              Knowledge bank
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4" data-testid="knowledge-attribution">
            {citedEntries.length > 0 ? (
              <div className="space-y-2">
                {citedEntries.map((entry) => (
                  <div key={entry.id} className="flex items-start gap-2">
                    <Badge
                      variant="secondary"
                      className="text-[10px] whitespace-nowrap shrink-0"
                    >
                      {entry.tier === "ORG"
                        ? "All locations"
                        : `${practiceShortNameById(entry.practiceId)} only`}
                    </Badge>
                    <div className="text-sm text-gray-800">
                      <Link
                        to={knowledgeEntryPath(entry.id)}
                        data-testid={`kb-entry-link-${entry.id}`}
                        className="hover:underline text-brand"
                      >
                        “{entry.question}”
                      </Link>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-gray-500 pt-1">
                  {call.followUpTask
                    ? "Not every question on this call had a knowledge bank answer — a follow-up task covers the rest. "
                    : ""}
                  Each cited reply is marked in the transcript below, and every
                  entry above opens in the{" "}
                  <Link
                    to={KNOWLEDGE_BANK_PATH}
                    className="text-brand hover:underline"
                  >
                    knowledge bank
                  </Link>
                  .
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-700">
                No knowledge bank answer was available for this question
                {call.followUpTask ? " — a follow-up task was created." : "."}{" "}
                The assistant never improvises answers outside the
                knowledge bank.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Follow-up task */}
        {call.followUpTask && (
          <Card
            className={`gap-2 py-4 ${
              taskDone ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"
            }`}
          >
            <CardHeader className="px-4">
              <CardTitle className="text-sm flex items-center justify-between gap-2">
                <span>Follow-up task</span>
                <Badge
                  variant="outline"
                  className={
                    taskDone
                      ? "bg-green-100 text-green-800 border-green-300"
                      : "bg-amber-100 text-amber-800 border-amber-300"
                  }
                >
                  {taskDone ? "Done" : "Pending"}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 space-y-3">
              <p className="text-sm text-gray-800">
                {call.followUpTask.description}
              </p>
              <div>
                <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">
                  Information collected on the call
                </h4>
                <ul className="list-disc list-inside text-sm text-gray-800 space-y-0.5">
                  {call.followUpTask.patientInfoCollected.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs text-gray-600">
                  Callback: {call.followUpTask.callbackNumber} · due by{" "}
                  {formatCallTime(call.followUpTask.dueBy, INBOUND_DEMO_TIMEZONE)}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  data-testid="mark-task-done"
                  onClick={() => setTaskDone(!taskDone)}
                >
                  {taskDone ? "Reopen" : "Mark done"}
                </Button>
              </div>
              <p className="text-[10px] text-gray-500 flex items-center gap-1">
                <Info className="h-3 w-3" />
                Demo action — not saved, resets on reload.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Transcript */}
        <Card className="gap-0 py-2">
          <CardContent className="px-4">
            <Accordion type="single" collapsible defaultValue="transcript">
              <AccordionItem value="transcript" className="border-b-0">
                <AccordionTrigger className="text-sm">
                  Full transcript
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2.5" data-testid="call-transcript">
                    {call.transcript.map((turn, index) => {
                      const isAgent = turn.speaker === "AGENT";
                      const entry =
                        turn.knowledgeEntryId === undefined
                          ? undefined
                          : knowledgeEntryById(turn.knowledgeEntryId);
                      const isExpanded = expandedCitations[index] === true;
                      return (
                        <div key={index} className="flex gap-2.5">
                          <span className="text-[10px] text-gray-400 w-9 shrink-0 pt-2.5 text-right tabular-nums">
                            {formatOffset(turn.offsetSeconds)}
                          </span>
                          <div
                            className={`flex-1 rounded-lg border px-3 py-2 ${
                              isAgent
                                ? "bg-brand-50 border-brand-100"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <div
                              className={`text-[10px] font-semibold uppercase tracking-wide mb-1 ${
                                isAgent ? "text-brand" : "text-gray-500"
                              }`}
                            >
                              {isAgent ? "Agent" : "Caller"}
                            </div>
                            <p className="text-sm text-gray-800">{turn.text}</p>
                            {entry && (
                              <div className="mt-2 pt-2 border-t border-brand-100">
                                <button
                                  type="button"
                                  aria-expanded={isExpanded}
                                  data-testid={`kb-citation-toggle-${index}`}
                                  className="flex items-center gap-1.5 text-[11px] font-medium text-brand hover:underline"
                                  onClick={() => toggleCitation(index)}
                                >
                                  <BookOpenCheck className="h-3.5 w-3.5 shrink-0" />
                                  {entry.tier === "ORG"
                                    ? "Organization knowledge bank"
                                    : "Practice knowledge bank"}
                                  <ChevronDown
                                    className={`h-3 w-3 transition-transform ${
                                      isExpanded ? "rotate-180" : ""
                                    }`}
                                  />
                                </button>
                                {isExpanded && (
                                  <div
                                    className="mt-2 rounded-md border border-brand-100 bg-white px-2.5 py-2 space-y-1.5"
                                    data-testid={`kb-citation-${index}`}
                                  >
                                    <p className="text-[11px] font-medium text-brand">
                                      {knowledgeSourceLabel(entry)}
                                    </p>
                                    <p className="text-xs text-gray-800">
                                      <span className="text-gray-500">
                                        Cited entry:{" "}
                                      </span>
                                      “{entry.question}”
                                    </p>
                                    <p className="text-xs text-gray-700">
                                      {entry.answer}
                                    </p>
                                    <p className="text-[10px] text-gray-500">
                                      Last updated {entry.lastReviewedAt}
                                    </p>
                                    <Link
                                      to={knowledgeEntryPath(entry.id)}
                                      data-testid={`kb-citation-link-${index}`}
                                      className="inline-flex items-center gap-1 text-[11px] font-medium text-brand hover:underline"
                                    >
                                      Open in knowledge bank
                                      <ArrowUpRight className="h-3 w-3 shrink-0" />
                                    </Link>
                                  </div>
                                )}
                              </div>
                            )}
                            {turn.appointmentTypeCited && (
                              <div className="mt-2 pt-2 border-t border-emerald-200">
                                <button
                                  type="button"
                                  aria-expanded={isExpanded}
                                  data-testid={`appt-type-citation-toggle-${index}`}
                                  className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 hover:underline"
                                  onClick={() => toggleCitation(index)}
                                >
                                  <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                                  Appointment types
                                  <ChevronDown
                                    className={`h-3 w-3 transition-transform ${
                                      isExpanded ? "rotate-180" : ""
                                    }`}
                                  />
                                </button>
                                {isExpanded && (
                                  <div
                                    className="mt-2 rounded-md border border-emerald-200 bg-white px-2.5 py-2 space-y-1.5"
                                    data-testid={`appt-type-citation-${index}`}
                                  >
                                    <p className="text-[11px] font-medium text-emerald-700">
                                      Matched configured appointment type
                                    </p>
                                    <p className="text-xs text-gray-800">
                                      <span className="text-gray-500">
                                        Appointment type:{" "}
                                      </span>
                                      {turn.appointmentTypeCited.name}
                                    </p>
                                    <p className="text-xs text-gray-700">
                                      {turn.appointmentTypeCited.description}
                                    </p>
                                    <p className="text-[10px] text-gray-500">
                                      Duration: {turn.appointmentTypeCited.durationMinutes} minutes
                                    </p>
                                    <Link
                                      to={APPOINTMENT_TYPES_PATH}
                                      data-testid={`appt-type-citation-link-${index}`}
                                      className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 hover:underline"
                                    >
                                      Open appointment types
                                      <ArrowUpRight className="h-3 w-3 shrink-0" />
                                    </Link>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="mt-4 pt-3 border-t text-xs text-gray-500 flex items-center gap-1.5">
                    <Volume2 className="h-3.5 w-3.5" />
                    Call audio will be available in the production product.
                    This demo shows transcripts only.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default InboundCallDetail;
